import { Injectable } from '@angular/core';
import { FocusPosition } from '../_model/tv-focus-memory.model';

/**
 * TVFocusMemoryService - Persist focus positions across navigation
 *
 * Provides two storage tiers:
 * - Session storage: Focus positions within current browser session (NAV-11)
 * - Persistent storage: Cross-session focus positions with 7-day expiry (NAV-12)
 *
 * Supports quota management and automatic cleanup of old entries.
 */
@Injectable({ providedIn: 'root' })
export class TVFocusMemoryService {
  private readonly SESSION_KEY = 'tv_focus_session';
  private readonly PERSISTENT_KEY = 'tv_focus_persistent';
  private readonly SESSION_LIMIT = 50;
  private readonly SESSION_TRIM_TO = 10;
  private readonly PERSISTENT_LIMIT = 20;
  private readonly EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  constructor() {}

  /* ========== SESSION STORAGE (NAV-11) ========== */

  /**
   * Save focus position to session storage (cleared on tab close)
   * Keyed by route. Automatically trims old entries when limit is exceeded.
   */
  saveFocusPosition(position: FocusPosition): void {
    const positions = this.loadPositions(sessionStorage, this.SESSION_KEY);
    positions[position.route] = position;

    // Trim if over limit
    const routes = Object.keys(positions);
    if (routes.length > this.SESSION_LIMIT) {
      const sorted = routes.sort((a, b) =>
        positions[a].timestamp - positions[b].timestamp
      );
      const toKeep = sorted.slice(-this.SESSION_TRIM_TO);
      const trimmed: Record<string, FocusPosition> = {};
      toKeep.forEach(route => {
        trimmed[route] = positions[route];
      });
      this.savePositions(sessionStorage, this.SESSION_KEY, trimmed);
    } else {
      this.savePositions(sessionStorage, this.SESSION_KEY, positions);
    }
  }

  /**
   * Restore focus position from session storage
   * Returns null if not found
   */
  restoreFocusPosition(route: string): FocusPosition | null {
    const positions = this.loadPositions(sessionStorage, this.SESSION_KEY);
    return positions[route] || null;
  }

  /* ========== PERSISTENT STORAGE (NAV-12) ========== */

  /**
   * Save focus position to persistent storage (survives tab close)
   * Keyed by route. 7-day expiry. Automatically trims old entries.
   */
  savePersistentPosition(position: FocusPosition): void {
    const positions = this.loadPositions(localStorage, this.PERSISTENT_KEY);
    positions[position.route] = position;

    // Trim if over limit (remove oldest by timestamp)
    const routes = Object.keys(positions);
    if (routes.length > this.PERSISTENT_LIMIT) {
      const sorted = routes.sort((a, b) =>
        positions[a].timestamp - positions[b].timestamp
      );
      const toKeep = sorted.slice(-this.PERSISTENT_LIMIT);
      const trimmed: Record<string, FocusPosition> = {};
      toKeep.forEach(route => {
        trimmed[route] = positions[route];
      });
      this.savePositions(localStorage, this.PERSISTENT_KEY, trimmed);
    } else {
      this.savePositions(localStorage, this.PERSISTENT_KEY, positions);
    }
  }

  /**
   * Restore focus position from persistent storage
   * Returns null if not found or expired (7 days)
   */
  restorePersistentPosition(route: string): FocusPosition | null {
    const positions = this.loadPositions(localStorage, this.PERSISTENT_KEY);
    const position = positions[route];

    if (!position) return null;

    // Check expiry
    const age = Date.now() - position.timestamp;
    if (age > this.EXPIRY_MS) {
      return null;
    }

    return position;
  }

  /* ========== CLEANUP ========== */

  /**
   * Clear session storage
   */
  clearSession(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Clear persistent storage
   */
  clearPersistent(): void {
    localStorage.removeItem(this.PERSISTENT_KEY);
  }

  /**
   * Clear both session and persistent storage
   */
  clearAll(): void {
    this.clearSession();
    this.clearPersistent();
  }

  /* ========== PRIVATE HELPERS ========== */

  /**
   * Load positions from storage with fallback to empty object
   */
  private loadPositions(storage: Storage, key: string): Record<string, FocusPosition> {
    try {
      const data = storage.getItem(key);
      if (!data) return {};
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  }

  /**
   * Save positions to storage with quota error handling
   * On QuotaExceededError, keeps only the 10 most recent routes
   */
  private savePositions(
    storage: Storage,
    key: string,
    positions: Record<string, FocusPosition>
  ): void {
    try {
      storage.setItem(key, JSON.stringify(positions));
    } catch (e: any) {
      if (e.name === 'QuotaExceededError') {
        // Trim to last 10 routes and retry
        const routes = Object.keys(positions);
        const sorted = routes.sort((a, b) =>
          positions[a].timestamp - positions[b].timestamp
        );
        const toKeep = sorted.slice(-10);
        const trimmed: Record<string, FocusPosition> = {};
        toKeep.forEach(route => {
          trimmed[route] = positions[route];
        });
        try {
          storage.setItem(key, JSON.stringify(trimmed));
        } catch (retryError) {
          // If still failing, clear completely
          storage.removeItem(key);
        }
      }
    }
  }
}
